import React, { useContext } from 'react';
import Bugsnag from '@bugsnag/js';
import { FlexBox, H2, H3, Typography } from '@vp/swan';
import { __ } from '@99designs/i18n';
import { BriefContext } from '../BriefContext';
import IconContainer from './components/IconContainer';
import NumberIcon from './components/NumberIcon';
import StepDescriptionBox from './components/StepDescriptionBox';
import StepIconWrapper from './components/StepIconWrapper';
import StepItemContainer from './components/StepItemContainer';
import StepTitleContainer from './components/StepTitleContainer';
import StepsContainer from './components/StepsContainer';

export type HowItWorksStep = {
    title: string;
    description: string;
    image: React.ReactNode;
};

const HIW_ASSET_URL =
    'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/expert_services/Brief/HowItWorks';

function getSteps(howItWorks?: string): HowItWorksStep[] {
    switch (howItWorks) {
        case 'care-express-hiw':
            return [
                {
                    title: __('Upload your design', { _context: 'how it works step title' }),
                    description: __(
                        'Send your custom shape artwork so we can adjust the cutline.',
                        { _context: 'how it works step description' }
                    ),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/UploadAndGoStep1.png`}
                            alt={__('step 1')}
                            width={'90%'}
                            height={'auto'}
                        />
                    ),
                },
                {
                    title: __('Receive your file', { _context: 'how it works step title' }),
                    description: __('Get your file with the new cutline in 4 hours or less.', {
                        _context: 'how it works step description',
                    }),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/UploadAndGoStep2.png`}
                            alt={__('step 2')}
                            width={'100%'}
                            height={'auto'}
                        />
                    ),
                },
                {
                    title: __('Checkout and purchase', { _context: 'how it works step title' }),
                    description: __("Once you're ready to purchase, place your order.", {
                        _context: 'how it works step description',
                    }),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/UploadAndGoStep3.png`}
                            alt={__('step 3')}
                            width={'100%'}
                            height={'auto'}
                        />
                    ),
                },
            ];
        case 'community-matching-collaborate-hiw':
        case 'bifrost-hiw':
            return [
                {
                    title: __('Describe what you need', { _context: 'how it works step title' }),
                    description: __('Fill out this brief with your project details.', {
                        _context: 'how it works step description',
                    }),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/CommunityStep1.png`}
                            alt={__('step 1')}
                            width={'90%'}
                            height={'auto'}
                        />
                    ),
                },
                {
                    title: __('Select your designer', { _context: 'how it works step title' }),
                    description: __(
                        "View designers' pitches, reviews and portfolios to pick the right one for you.",
                        { _context: 'how it works step description' }
                    ),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/CommunityStep2.png`}
                            alt={__('step 2')}
                            width={'100%'}
                            height={'auto'}
                        />
                    ),
                },
                {
                    title: __('Your project kicks off', { _context: 'how it works step title' }),
                    description: __(
                        'Your selected designer will message you with any questions and concepts.',
                        { _context: 'how it works step description' }
                    ),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/CommunityStep3.png`}
                            alt={__('step 3')}
                            width={'100%'}
                            height={'auto'}
                        />
                    ),
                },
            ];
        default:
            return [
                {
                    title: __('Describe what you need', { _context: 'how it works step title' }),
                    description: __('Fill out this brief with your project details', {
                        _context: 'how it works step description',
                    }),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/Step1.png`}
                            alt={__('step 1')}
                            width={'90%'}
                            height={'auto'}
                        />
                    ),
                },
                {
                    title: __('Submit your request', { _context: 'how it works step title' }),
                    description: __('Pay for your design & send your brief to our team', {
                        _context: 'how it works step description',
                    }),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/Step2.png`}
                            alt={__('step 2')}
                            width={'100%'}
                            height={'auto'}
                        />
                    ),
                },
                {
                    title: __('Receive your design', { _context: 'how it works step title' }),
                    description: __('Get a draft from your designer within 24 hours', {
                        _context: 'how it works step description',
                    }),
                    image: (
                        <img
                            src={`${HIW_ASSET_URL}/Step3.png`}
                            alt={__('step 3')}
                            width={'100%'}
                            height={'auto'}
                        />
                    ),
                },
            ];
    }
}

interface HowItWorksProps {
    isModal?: boolean;
    mdStepsDirection?: 'row' | 'column';
    mdStepItemDirection?: 'row' | 'column';
    isIntelligentBrief?: boolean;
    textAlign?: 'left' | 'center' | 'right';
}
export function HowItWorks({
    isModal = false,
    mdStepsDirection,
    mdStepItemDirection,
    isIntelligentBrief = false,
    textAlign,
}: HowItWorksProps) {
    const ctx = useContext(BriefContext);

    if (!ctx) {
        // While this component should be used inside BriefContext, it's not worth breaking the page if it's not
        Bugsnag.notify(new Error('HowItWorks component used outside of BriefContext'));
    }
    const steps: HowItWorksStep[] = getSteps(ctx?.config?.howItWorks);
    return (
        <div style={{ width: '100%' }}>
            <H2 fontSkin={'title-subsection'} textAlign="left" pr={8}>
                {__('How it works')}
            </H2>
            <StepsContainer isModal={isModal} mdStepsDirection={mdStepsDirection}>
                {steps.map(({ title, image, description }, index) => (
                    <StepItemContainer
                        key={`how-it-works-${index}`}
                        isModal={isModal}
                        mdStepItemDirection={mdStepItemDirection}
                    >
                        <StepIconWrapper>{image}</StepIconWrapper>
                        <FlexBox flexDirection={'column'} justifyContent={'center'}>
                            <StepTitleContainer isModal={isModal}>
                                <IconContainer>
                                    <NumberIcon num={index + 1} />
                                </IconContainer>
                                <H3
                                    fontSkin={'body-standard-bold'}
                                    textAlign="left"
                                    p={0}
                                    m={0}
                                    ml={2}
                                >
                                    {title}
                                </H3>
                            </StepTitleContainer>
                            <StepDescriptionBox
                                isModal={isModal}
                                isIntelligentBrief={isIntelligentBrief}
                            >
                                <Typography textAlign={textAlign} fontSkin={'body-small'}>
                                    {description}
                                </Typography>
                            </StepDescriptionBox>
                        </FlexBox>
                    </StepItemContainer>
                ))}
            </StepsContainer>
        </div>
    );
}
